import { Component, Inject, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CarriersService, FedExVerificationData } from '../../services/carriers.service';
import { PdqInputCodeVerificationComponent } from '@storybook/components/input-code-verification/input-code-verification.component';

export interface FedExVerificationDialogData {
  carrierId: number;
  verificationMethod: 'SMS' | 'CALL' | 'EMAIL';
}

@Component({
  selector: 'app-fedex-verification-dialog',
  templateUrl: './fedex-verification.dialog.html',
  styleUrls: ['./fedex-verification.dialog.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class FedExVerificationDialog implements OnDestroy {
  @ViewChild(PdqInputCodeVerificationComponent) codeVerification: PdqInputCodeVerificationComponent;

  get canSubmit(): boolean {
    return this.isPinComplete;
  }

  carrierId: number;
  verificationMethod: string;
  isLoading = false;
  errorMessage = '';
  isPinComplete = false;

  private pinCode = '';
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<FedExVerificationDialog>,
    @Inject(MAT_DIALOG_DATA) public data: FedExVerificationDialogData,
    private carriersService: CarriersService,
  ) {
    this.carrierId = data.carrierId;
    this.verificationMethod = data.verificationMethod;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getMethodDisplayName(): string {
    switch (this.verificationMethod) {
      case 'SMS':
        return 'SMS';
      case 'CALL':
        return 'phone call';
      case 'EMAIL':
        return 'email';
      default:
        return '';
    }
  }

  onPinComplete(code: string): void {
    this.pinCode = code;
    this.isPinComplete = true;
  }

  onVerify(): void {
    if (!this.canSubmit || this.isLoading) return;

    this.isLoading = true;
    this.errorMessage = '';

    const verificationData: FedExVerificationData = {
      verificationType: 'PIN',
      pin: this.pinCode,
    };

    this.carriersService
      .completeCarrierVerification(this.carrierId, verificationData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response?.status === 'Succeeded') {
            this.dialogRef.close({ success: true });
          } else {
            this.errorMessage = response?.message || 'Verification failed. Please try again.';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error?.error?.message || 'Verification failed. Please try again.';
        },
      });
  }

  onResend(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.errorMessage = '';

    this.carriersService
      .resendCarrierVerification(this.carrierId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.isPinComplete = false;
          this.pinCode = '';
          this.codeVerification?.reset();
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'Failed to resend verification code.';
        },
      });
  }

  onClose(): void {
    this.dialogRef.close({ success: false });
  }
}
